import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Zap, Brain, Cpu, Globe, Rocket, BookOpen, Award, Plus,
  TrendingUp, DollarSign, Clock, ExternalLink, Star, Target,
  Sparkles, Shield, Database, Code, BarChart, Users, Play, Info, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AI-Proof and AI-Enhanced Careers
const FUTURE_CAREERS = [
  {
    id: 'ai_engineer',
    title: 'AI/ML Engineer',
    category: 'tech',
    salary: '$120,000 - $250,000',
    growth: '+40% by 2030',
    description: 'Design and build AI systems, machine learning models, and intelligent applications.',
    skills: ['Python', 'TensorFlow/PyTorch', 'Statistics', 'Data Structures', 'Cloud Platforms'],
    aiImpact: 'high_demand',
    icon: Brain,
    color: 'purple',
  },
  {
    id: 'prompt_engineer',
    title: 'Prompt Engineer',
    category: 'tech',
    salary: '$80,000 - $180,000',
    growth: 'NEW FIELD',
    description: 'Craft effective prompts to get optimal outputs from AI models. Critical for AI adoption.',
    skills: ['Communication', 'Critical Thinking', 'AI Understanding', 'Technical Writing', 'Testing'],
    aiImpact: 'high_demand',
    icon: Sparkles,
    color: 'pink',
  },
  {
    id: 'robotics_tech',
    title: 'Robotics Technician',
    category: 'tech',
    salary: '$55,000 - $95,000',
    growth: '+25% by 2030',
    description: 'Install, maintain, and repair robotic systems in manufacturing, healthcare, and logistics.',
    skills: ['Electronics', 'Mechanical Systems', 'Programming', 'Troubleshooting', 'Safety Protocols'],
    aiImpact: 'high_demand',
    icon: Cpu,
    color: 'cyan',
  },
  {
    id: 'data_scientist',
    title: 'Data Scientist',
    category: 'tech',
    salary: '$100,000 - $180,000',
    growth: '+35% by 2030',
    description: 'Extract insights from data to drive business decisions and train AI models.',
    skills: ['Python/R', 'Statistics', 'Machine Learning', 'Data Visualization', 'Business Acumen'],
    aiImpact: 'enhanced',
    icon: Database,
    color: 'blue',
  },
  {
    id: 'cybersecurity',
    title: 'AI Security Specialist',
    category: 'tech',
    salary: '$110,000 - $200,000',
    growth: '+32% by 2030',
    description: 'Protect AI systems from attacks, ensure ethical AI deployment, and audit AI decisions.',
    skills: ['Security Fundamentals', 'AI/ML Knowledge', 'Ethics', 'Risk Assessment', 'Compliance'],
    aiImpact: 'high_demand',
    icon: Shield,
    color: 'red',
  },
  {
    id: 'healthcare_ai',
    title: 'Healthcare AI Specialist',
    category: 'healthcare',
    salary: '$90,000 - $160,000',
    growth: '+28% by 2030',
    description: 'Implement AI solutions in healthcare for diagnostics, treatment planning, and operations.',
    skills: ['Healthcare Knowledge', 'AI Understanding', 'Data Analysis', 'Regulatory Compliance', 'Communication'],
    aiImpact: 'high_demand',
    icon: Target,
    color: 'emerald',
  },
  {
    id: 'renewable_energy',
    title: 'Renewable Energy Tech',
    category: 'trades',
    salary: '$50,000 - $90,000',
    growth: '+52% by 2030',
    description: 'Install and maintain solar panels, wind turbines, and smart grid systems.',
    skills: ['Electrical', 'Mechanical', 'Safety', 'Problem Solving', 'Physical Fitness'],
    aiImpact: 'ai_resistant',
    icon: Zap,
    color: 'amber',
  },
  {
    id: 'ev_technician',
    title: 'EV Technician',
    category: 'trades',
    salary: '$55,000 - $85,000',
    growth: '+60% by 2030',
    description: 'Repair and maintain electric vehicles, charging infrastructure, and battery systems.',
    skills: ['Automotive', 'Electronics', 'High-Voltage Safety', 'Diagnostics', 'Continuous Learning'],
    aiImpact: 'ai_resistant',
    icon: Rocket,
    color: 'green',
  },
];

// Agentic FuturePath AI Certifications
const CERTIFICATIONS = [
  {
    id: 'ai_fundamentals',
    name: 'AI Fundamentals Certificate',
    duration: '4 weeks',
    level: 'Beginner',
    description: 'Understand AI concepts, applications, and how to work alongside AI tools.',
    modules: ['What is AI?', 'AI in Daily Life', 'AI Tools Overview', 'Future of Work with AI'],
    price: '$49',
    badge: '🎓',
  },
  {
    id: 'prompt_mastery',
    name: 'Prompt Engineering Mastery',
    duration: '6 weeks',
    level: 'Intermediate',
    description: 'Learn to effectively communicate with AI to get optimal results.',
    modules: ['Prompt Basics', 'Advanced Techniques', 'Industry Applications', 'Best Practices'],
    price: '$99',
    badge: '✨',
  },
  {
    id: 'ai_career_ready',
    name: 'AI Career Readiness',
    duration: '8 weeks',
    level: 'Advanced',
    description: 'Complete preparation for AI-enhanced careers with hands-on projects.',
    modules: ['AI Tools Mastery', 'Building with AI', 'AI Ethics', 'Career Portfolio', 'Interview Prep'],
    price: '$149',
    badge: '🚀',
  },
];

// AI Impact Categories
const AI_IMPACT = {
  high_demand: { label: 'High Demand (AI creates jobs)', color: 'green', icon: TrendingUp },
  enhanced: { label: 'AI Enhanced (Work with AI)', color: 'blue', icon: Sparkles },
  ai_resistant: { label: 'AI Resistant (Human-required)', color: 'amber', icon: Shield },
};

export default function FutureCareersPage() {
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('careers');
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const addToGoals = async (item, category = 'career') => {
    try {
      await axios.post(`${API}/goals`, {
        title: item.title || item.name,
        description: item.description,
        category: category,
        priority: 'medium',
        status: 'backlog',
        source: 'Future Careers'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Added to your Goals! 🎯');
    } catch (error) {
      toast.error('Failed to add to goals');
    }
  };

  const filteredCareers = filterCategory === 'all' 
    ? FUTURE_CAREERS 
    : FUTURE_CAREERS.filter(c => c.category === filterCategory);

  return (
    <AppLayout>
      <PageHeader 
        title="Future Careers Hub"
        description="Discover AI-proof careers and get certified for the future of work"
      />

      {/* Hero Banner */}
      <GlassCard className="mb-8 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-500/30">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-2">
              <Sparkles className="inline w-6 h-6 mr-2 text-purple-400" />
              The AI Revolution is Here
            </h2>
            <p className="text-slate-300">
              AI is transforming every industry. Some jobs will disappear, but many more will be created.
              The key is understanding which skills will be valuable and preparing now.
            </p>
            <div className="flex gap-3 mt-4">
              <Button onClick={() => setActiveTab('certifications')} className="bg-purple-600 hover:bg-purple-700">
                <Award className="w-4 h-4 mr-2" />
                Get Certified
              </Button>
              <Button variant="outline" className="border-purple-500 text-purple-300">
                <Play className="w-4 h-4 mr-2" />
                Watch Overview
              </Button>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="w-32 h-32 bg-purple-500/20 rounded-full flex items-center justify-center">
              <Brain className="w-16 h-16 text-purple-400" />
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'careers', label: 'Future Careers', icon: Rocket },
          { id: 'certifications', label: 'FuturePath Certifications', icon: Award },
          { id: 'resources', label: 'Learning Resources', icon: BookOpen },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-navy-800 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Careers Tab */}
      {activeTab === 'careers' && (
        <>
          {/* Filters */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'all', label: 'All Careers' },
              { id: 'tech', label: 'Technology' },
              { id: 'healthcare', label: 'Healthcare' },
              { id: 'trades', label: 'Skilled Trades' },
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => setFilterCategory(filter.id)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  filterCategory === filter.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-navy-800 text-slate-300 hover:text-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* AI Impact Legend */}
          <div className="flex flex-wrap gap-4 mb-6 p-4 bg-navy-800/50 rounded-xl">
            {Object.entries(AI_IMPACT).map(([key, impact]) => {
              const Icon = impact.icon;
              return (
                <div key={key} className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 text-${impact.color}-400`} />
                  <span className="text-sm text-slate-300">{impact.label}</span>
                </div>
              );
            })}
          </div>

          {/* Careers Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {filteredCareers.map(career => {
              const Icon = career.icon;
              const impact = AI_IMPACT[career.aiImpact];
              const ImpactIcon = impact.icon;
              
              return (
                <GlassCard 
                  key={career.id}
                  className="hover:border-purple-500/50 transition-all cursor-pointer"
                  onClick={() => setSelectedCareer(selectedCareer?.id === career.id ? null : career)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 bg-${career.color}-500/20 rounded-xl flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 text-${career.color}-400`} />
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full bg-${impact.color}-500/20`}>
                      <ImpactIcon className={`w-3 h-3 text-${impact.color}-400`} />
                      <span className={`text-xs text-${impact.color}-400`}>{career.aiImpact.replace('_', ' ')}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-1">{career.title}</h3>
                  <p className="text-slate-300 text-sm mb-4">{career.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-emerald-400">
                      <DollarSign className="w-4 h-4" />
                      {career.salary}
                    </div>
                    <div className="flex items-center gap-1 text-blue-400">
                      <TrendingUp className="w-4 h-4" />
                      {career.growth}
                    </div>
                  </div>
                  
                  {selectedCareer?.id === career.id && (
                    <div className="mt-4 pt-4 border-t border-navy-700 space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-300 mb-2">Key Skills:</h4>
                        <div className="flex flex-wrap gap-2">
                          {career.skills.map(skill => (
                            <span key={skill} className="px-2 py-1 bg-navy-700 text-slate-300 text-xs rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          addToGoals(career);
                        }}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add to My Goals
                      </Button>
                    </div>
                  )}
                </GlassCard>
              );
            })}
          </div>
        </>
      )}

      {/* Certifications Tab */}
      {activeTab === 'certifications' && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Agentic FuturePath AI Certifications</h2>
            <p className="text-slate-300">Validate your skills and stand out in the AI-driven job market</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {CERTIFICATIONS.map(cert => (
              <GlassCard key={cert.id} className="flex flex-col">
                <div className="text-4xl mb-4">{cert.badge}</div>
                <h3 className="text-xl font-bold text-white mb-1">{cert.name}</h3>
                <div className="flex items-center gap-3 text-sm text-slate-300 mb-3">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {cert.duration}
                  </span>
                  <span className="px-2 py-0.5 bg-navy-700 rounded-full">{cert.level}</span>
                </div>
                <p className="text-slate-300 text-sm mb-4 flex-1">{cert.description}</p>
                
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-300 mb-2">What you'll learn:</h4>
                  <ul className="space-y-1">
                    {cert.modules.map((module, i) => (
                      <li key={i} className="text-sm text-slate-300 flex items-center gap-2">
                        <Star className="w-3 h-3 text-purple-400" />
                        {module}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-auto pt-4 border-t border-navy-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl font-bold text-white">{cert.price}</span>
                    <span className="text-slate-300 text-sm">one-time</span>
                  </div>
                  <Button
                    onClick={() => addToGoals({ title: `Complete ${cert.name}`, description: cert.description }, 'certification')}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Goals
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
          
          <div className="text-center mt-8 p-6 bg-purple-900/20 rounded-xl border border-purple-500/30">
            <h3 className="text-lg font-semibold text-white mb-2">Coming Soon: Full Certification Program</h3>
            <p className="text-slate-300 mb-4">
              We're building comprehensive certification courses with video lessons, hands-on projects, and mentorship.
            </p>
            <Button variant="outline" className="border-purple-500 text-purple-300">
              Join Waitlist
            </Button>
          </div>
        </div>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <div className="grid md:grid-cols-2 gap-6">
          <GlassCard>
            <BookOpen className="w-10 h-10 text-blue-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Free Learning Resources</h3>
            <ul className="space-y-3">
              {[
                { name: 'Google AI Essentials', url: 'https://grow.google/certificates/ai-essentials/', desc: 'Free Google certification' },
                { name: 'Microsoft AI Skills', url: 'https://learn.microsoft.com/en-us/training/paths/get-started-with-artificial-intelligence/', desc: 'AI fundamentals course' },
                { name: 'Coursera AI Courses', url: 'https://www.coursera.org/courses?query=artificial%20intelligence', desc: 'Audit for free' },
                { name: 'Khan Academy', url: 'https://www.khanacademy.org/computing', desc: 'Computing basics' },
              ].map(resource => (
                <li key={resource.name}>
                  <a 
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg hover:bg-navy-800 transition-colors"
                  >
                    <div>
                      <span className="text-white">{resource.name}</span>
                      <span className="text-slate-300 text-sm block">{resource.desc}</span>
                    </div>
                    <ExternalLink className="w-4 h-4 text-blue-400" />
                  </a>
                </li>
              ))}
            </ul>
          </GlassCard>
          
          <GlassCard>
            <Code className="w-10 h-10 text-purple-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">AI Tools to Learn</h3>
            <ul className="space-y-3">
              {[
                { name: 'ChatGPT', url: 'https://chat.openai.com', desc: 'Conversational AI' },
                { name: 'Claude', url: 'https://claude.ai', desc: 'AI assistant' },
                { name: 'Midjourney', url: 'https://midjourney.com', desc: 'AI image generation' },
                { name: 'GitHub Copilot', url: 'https://github.com/features/copilot', desc: 'AI coding assistant' },
              ].map(tool => (
                <li key={tool.name}>
                  <a 
                    href={tool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg hover:bg-navy-800 transition-colors"
                  >
                    <div>
                      <span className="text-white">{tool.name}</span>
                      <span className="text-slate-300 text-sm block">{tool.desc}</span>
                    </div>
                    <ExternalLink className="w-4 h-4 text-purple-400" />
                  </a>
                </li>
              ))}
            </ul>
          </GlassCard>
          
          <GlassCard className="md:col-span-2">
            <BarChart className="w-10 h-10 text-amber-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-4">Want the real numbers for your field?</h3>
            <p className="text-sm text-slate-300 max-w-2xl">
              Broad "AI will create 97 million jobs" headlines don't tell you anything about your own
              work. Future of Work scores automation exposure task by task for a specific occupation,
              using federal occupation data, what's exposed, what's rising in its place, and how much
              runway you have.
            </p>
            <Link to="/app/future-of-work" data-testid="future-careers-to-fow"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-cyan-300 text-slate-950 text-sm font-semibold hover:bg-cyan-200 transition-colors">
              Score my field <ArrowRight className="w-4 h-4" />
            </Link>
          </GlassCard>

          {/* O*NET & BLS Attribution */}
          <GlassCard className="md:col-span-2 bg-slate-900/30">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a href="https://services.onetcenter.org/" target="_blank" rel="noopener noreferrer" title="This site incorporates information from O*NET Web Services.">
                <img src="https://www.onetcenter.org/image/link/onet-in-it.svg" alt="O*NET in-it" className="h-10 opacity-70 hover:opacity-100 transition-opacity" />
              </a>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                This site incorporates information from{' '}
                <a href="https://services.onetcenter.org/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">O*NET Web Services</a>
                {' '}by the U.S. Department of Labor, Employment and Training Administration (USDOL/ETA). O*NET&reg; is a trademark of USDOL/ETA.
                Salary data provided by the U.S. Bureau of Labor Statistics.
              </p>
            </div>
          </GlassCard>
        </div>
      )}
    </AppLayout>
  );
}
